import { useState, type FormEvent } from "react";
import { Helmet } from "react-helmet-async";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import "./for-youth.css";

// usedibs.com/foryouth: a single screen with one email field. Signups land in
// youth_waitlist_signups and the team's Google Sheet (youth-waitlist-join).

const INVALID_EMAIL = "Please enter a valid email address.";
const FALLBACK_ERROR = "Something went wrong. Please try again.";
const EMAIL_PATTERN = /^[^\s@]+@(?:[^\s@.]+\.)+[^\s@.]{2,}$/;

/** `?ref=ig-bio` (or a UTM source) on the shared link: where the signup came from. */
function linkSource(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get("ref") ?? params.get("utm_source") ?? undefined;
}

async function readError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json();
      if (typeof body?.error === "string") return body.error;
    } catch {
      // Unreadable body: fall back below.
    }
  }
  return FALLBACK_ERROR;
}

const ForYouthWaitlist = () => {
  const [email, setEmail] = useState("");
  // Hidden from people; bots that fill every field get a silent no-op.
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "joined">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === "sending") return;
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError(INVALID_EMAIL);
      return;
    }
    setStatus("sending");
    setError(null);
    try {
      const { error: invokeError } = await supabase.functions.invoke("youth-waitlist-join", {
        body: { email: email.trim(), source: linkSource(), website },
      });
      if (invokeError) {
        setError(await readError(invokeError));
        setStatus("idle");
        return;
      }
      setStatus("joined");
    } catch {
      setError(FALLBACK_ERROR);
      setStatus("idle");
    }
  };

  return (
    <main className="fy-page">
      <Helmet>
        <title>Dibs for Youth · Join the waitlist</title>
        <meta name="description" content="Dibs for Youth is coming. Join the waitlist and be the first in." />
        <meta name="theme-color" content="#fff9f5" />
      </Helmet>

      <div className="fy-atmosphere" aria-hidden="true">
        <span className="fy-light fy-light-apricot" />
        <span className="fy-light fy-light-sage" />
        <span className="fy-light fy-light-blush" />
        <span className="fy-light fy-light-coral" />
        <span className="fy-ring" />
        <span className="fy-orbit fy-orbit-inner" />
        <span className="fy-orbit fy-orbit-outer" />
        <span className="fy-glow" />
        <img
          className="fy-train"
          src="/dibs_assets/youth-train.webp"
          alt=""
          width="2172"
          height="724"
        />
        <span className="fy-grain" />
      </div>

      <section className="fy-content">
        <span className="fy-brand">
          <img src="/dibs_assets/red_logo_with_transparent_bg.png" width="20" height="28" alt="" />
          Dibs
        </span>
        <h1 className="fy-title">Join the waitlist</h1>
        <p className="fy-subtitle">Dibs for Youth is coming. Be the first in.</p>

        <div className="fy-action" aria-live="polite">
          {status === "joined" ? (
            <p className="fy-joined">
              <span className="fy-joined-icon" aria-hidden="true">
                <Check size={14} strokeWidth={3} />
              </span>
              You're on the list. We'll email you at launch.
            </p>
          ) : (
            <form className="fy-form" onSubmit={submit} noValidate>
              <label htmlFor="fy-email" className="sr-only">
                Email address
              </label>
              <input
                id="fy-email"
                className="fy-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Your email address"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (error) setError(null);
                }}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "fy-error" : undefined}
                maxLength={254}
              />
              <input
                className="fy-trap"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
              />
              <button className="fy-button" type="submit" disabled={status === "sending"}>
                {status === "sending" ? "Joining…" : "Join"}
              </button>
            </form>
          )}
        </div>

        {error && (
          <p id="fy-error" className="fy-error" role="alert">
            {error}
          </p>
        )}

        <p className="fy-fineprint">
          We'll only email you about the launch.{" "}
          <a href="/privacy-policy">Privacy policy</a>
        </p>

        <section className="fy-partners" aria-labelledby="fy-partners-title">
          <h2 id="fy-partners-title" className="fy-partners-title">
            Innovation partners
          </h2>
          <ul className="fy-partner-list">
            <li className="fy-partner fy-partner-meta" aria-label="Meta">
              <img src="/dibs_assets/meta-logo.webp" alt="" width="400" height="81" />
              <span className="sr-only">Meta</span>
            </li>
            <li className="fy-partner" aria-label="NVIDIA Inception Program">
              <img src="/dibs_assets/nvidia-logo.svg" alt="" width="108" height="20" />
              <span>NVIDIA Inception Program</span>
            </li>
            <li className="fy-partner" aria-label="Stripe for Startups Program">
              <img src="/dibs_assets/stripe-logo.webp" alt="" width="360" height="150" />
              <span>Stripe for Startups Program</span>
            </li>
          </ul>
        </section>
      </section>
    </main>
  );
};

export default ForYouthWaitlist;
